import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  SimpleButton,
  LoadingButton,
  Input,
  TextArea,
  Select,
  FormDialog,
  Toast,
  NotificationsList,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsArrowLeft, BsPlus, BsBoxArrowUpRight, BsPaperclip, BsTrash, BsDownload } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";
import { usePppAiContext } from "../hooks/usePppAiContext";
import { useDownload } from "../hooks/useDownload";

const STATO_CFG = {
  bozza:      { cls: "bg-gray-100 text-gray-700" },
  pubblicato: { cls: "bg-green-100 text-green-700" },
  scaduto:    { cls: "bg-red-100 text-red-700" },
  revocato:   { cls: "bg-orange-100 text-orange-700" },
};

const STATO_RICHIESTA_CFG = {
  ricevuta:            { cls: "bg-yellow-100 text-yellow-700" },
  risposta_pubblicata: { cls: "bg-green-100 text-green-700" },
};

function StatoBadge({ stato, versione, _locate }) {
  const cfg = STATO_CFG[stato] ?? { cls: "" };
  return (
    <span className="flex items-center gap-1.5">
      <span className={`px-2 py-0.5 rounded text-sm font-medium ${cfg.cls}`}>
        {_locate(`ppp_avviso_stato_${stato}`) ?? stato}
      </span>
      {versione > 1 && (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
          v{versione}
        </span>
      )}
    </span>
  );
}

function StatoRichiestaBadge({ stato, _locate }) {
  const cfg = STATO_RICHIESTA_CFG[stato] ?? { cls: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      {_locate(`ppp_avviso_richiesta_stato_${stato}`) ?? stato}
    </span>
  );
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

function formatBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap">{value || "—"}</span>
    </div>
  );
}

function scadenzaInfo(termineProposte) {
  if (!termineProposte) return null;
  const scadenza = new Date(termineProposte);
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  const giorni = Math.round((scadenza - oggi) / 86400000);
  if (giorni < 0)  return { giorni, cls: "bg-red-50 border-red-300 text-red-700",      icon: "⚠", label: `Scaduto da ${Math.abs(giorni)} giorni` };
  if (giorni <= 7) return { giorni, cls: "bg-red-50 border-red-300 text-red-700",      icon: "⚠", label: `Scade tra ${giorni} giorni` };
  if (giorni <= 15)return { giorni, cls: "bg-amber-50 border-amber-300 text-amber-700",icon: "⏳", label: `Scade tra ${giorni} giorni` };
  return               { giorni, cls: "bg-blue-50 border-blue-200 text-blue-700",     icon: "📅", label: `${giorni} giorni alla scadenza` };
}

const TODAY = new Date().toISOString().slice(0, 10);

export default function AvvisoPubblicoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { _locate, setOpenForm, addToast } = useStateContext();
  const { can } = useAuthUser();
  usePppAiContext('ppp.avviso', { id });
  const api = usePppApi();
  const { download } = useDownload();
  const fileInputRef     = useRef(null);
  const attoRevocaRef    = useRef(null);

  const [avviso, setAvviso]             = useState(null);
  const [procedimenti, setProcedimenti] = useState([]);
  const [operatori, setOperatori]       = useState([]);
  const [richieste, setRichieste]       = useState([]);
  const [allegati, setAllegati]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);

  const [editErrors, setEditErrors]     = useState(null);
  const [pubErrors, setPubErrors]       = useState(null);
  const [opErrors, setOpErrors]         = useState(null);
  const [richErrors, setRichErrors]     = useState(null);
  const [rispErrors, setRispErrors]     = useState(null);
  const [revocaErrors, setRevocaErrors] = useState(null);
  const [allegatiErrors, setAllegatiErrors] = useState(null);

  const [showPubblica, setShowPubblica]           = useState(false);
  const [showNuovoOp, setShowNuovoOp]             = useState(false);
  const [showNuovaRich, setShowNuovaRich]         = useState(false);
  const [showPubblicaRisp, setShowPubblicaRisp]   = useState(null);
  const [showRevoca, setShowRevoca]               = useState(false);
  const [showElimina, setShowElimina]             = useState(false);

  const [editForm, setEditForm] = useState(null);
  const [pubForm, setPubForm]   = useState({
    dataPubblicazione: "",
    terminePresentazioneProposte: "",
    urlAmministrazioneTrasparente: "",
    urlBdncp: "",
    urlGuue: "",
    inviaNotificaEmail: false,
  });
  const [opForm, setOpForm]     = useState({
    ragioneSociale: "", email: "", pec: "", telefono: "", dataRegistrazione: TODAY, note: "",
  });
  const [richForm, setRichForm] = useState({
    operatoreId: "", oggetto: "", testo: "", dataRichiesta: TODAY,
  });
  const [rispForm, setRispForm] = useState({ testoRisposta: "", dataRisposta: TODAY, fileRisposta: null });
  const rispFileRef = React.useRef(null);
  const [revocaForm, setRevocaForm] = useState({
    dataRevoca: TODAY, motivoRevoca: "", fileAttoRevoca: null,
  });
  const [nuovoAllegatoNome, setNuovoAllegatoNome] = useState("");

  const loadAvviso = useCallback(() => {
    api.getAvvisoPubblico(id, {
      onSuccess: (data) => { setAvviso(data); setAllegati(data.allegati ?? []); setLoading(false); },
      onError:   () => setLoading(false),
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOperatori = useCallback(() => {
    api.listOperatoriAvviso(id, { onSuccess: (data) => setOperatori(data ?? []) });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRichieste = useCallback(() => {
    api.listRichiesteAvviso(id, { onSuccess: (data) => setRichieste(data ?? []) });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadAvviso();
    loadOperatori();
    loadRichieste();
    api.listProcedimenti({ onSuccess: (data) => setProcedimenti(data ?? []) });
  }, [loadAvviso, loadOperatori, loadRichieste]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = (a) => {
    setEditForm({
      oggetto:               a.oggetto ?? "",
      procedimentoId:        a.procedimentoId ?? "",
      descrizioneIntervento: a.descrizioneIntervento ?? "",
      requisitiMinimi:       a.requisitiMinimi ?? "",
      sopraSogliaEuropea:    a.sopraSogliaEuropea ?? false,
      numeroAtto:            a.numeroAtto ?? "",
      note:                  a.note ?? "",
    });
    setOpenForm(true);
  };

  const setField  = (set) => (field) => (e) => set((f) => ({ ...f, [field]: e.target.value }));
  const setEditF  = setField(setEditForm);
  const setOpF    = setField(setOpForm);
  const setRichF  = setField(setRichForm);
  const setRispF  = setField(setRispForm);
  const setCheck  = (field) => (e) => setEditForm((f) => ({ ...f, [field]: e.target.checked }));

  const handleAggiorna = () => {
    if (!editForm?.oggetto) return;
    setSaving(true); setEditErrors(null);
    api.aggiornaAvvisoPubblico(avviso.id, {
      ...editForm,
      procedimentoId: editForm.procedimentoId ? parseInt(editForm.procedimentoId) : null,
    }, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_avviso_aggiornato")} />);
        setSaving(false); setOpenForm(false); setEditErrors(null); loadAvviso();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setEditErrors(response.errors); },
    });
  };

  const handlePubblica = () => {
    if (!pubForm.dataPubblicazione || !pubForm.terminePresentazioneProposte) return;
    setSaving(true); setPubErrors(null);
    api.pubblicaAvvisoPubblico(avviso.id, pubForm, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_avviso_pubblicato_ok")} />);
        setSaving(false); setShowPubblica(false); setPubErrors(null); loadAvviso();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setPubErrors(response.errors); },
    });
  };

  const handleUploadAllegato = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const nome = nuovoAllegatoNome || file.name;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("nome", nome);
    setSaving(true); setAllegatiErrors(null);
    api.uploadAllegatoAvviso(avviso.id, fd, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_avviso_allegato_caricato")} />);
        setSaving(false); setNuovoAllegatoNome(""); loadAvviso();
        if (fileInputRef.current) fileInputRef.current.value = "";
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setAllegatiErrors(response.errors); },
    });
  };

  const handleEliminaAllegato = (allegatoId) => {
    api.eliminaAllegatoAvviso(avviso.id, allegatoId, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_avviso_allegato_eliminato")} />);
        loadAvviso();
      },
    });
  };

  const handleRevoca = () => {
    if (!revocaForm.dataRevoca || !revocaForm.motivoRevoca || !revocaForm.fileAttoRevoca) return;
    const fd = new FormData();
    fd.append("dataRevoca", revocaForm.dataRevoca);
    fd.append("motivoRevoca", revocaForm.motivoRevoca);
    fd.append("fileAttoRevoca", revocaForm.fileAttoRevoca);
    setSaving(true); setRevocaErrors(null);
    api.revocaAvviso(avviso.id, fd, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_avviso_revocato_ok")} />);
        setSaving(false); setShowRevoca(false);
        setRevocaForm({ dataRevoca: TODAY, motivoRevoca: "", fileAttoRevoca: null });
        loadAvviso();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setRevocaErrors(response.errors); },
    });
  };

  const handleElimina = () => {
    setSaving(true);
    api.eliminaAvvisoPubblico(avviso.id, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_avviso_eliminato_ok") ?? "Avviso eliminato con successo."} />);
        navigate("/ppp/avvisi-pubblici");
      },
      onError: () => setSaving(false),
    });
  };

  const handleCreaVersione = () => {
    setSaving(true);
    api.creaVersioneAvviso(avviso.id, {
      onSuccess: (data) => {
        addToast(<Toast duration={4} content={_locate("ppp_avviso_nuova_versione_ok")} />);
        setSaving(false);
        navigate(`/ppp/avvisi-pubblici/${data.id ?? data.nuovoAvvisoId ?? data}`);
      },
      onError: () => setSaving(false),
    });
  };

  const handleRegistraOperatore = () => {
    if (!opForm.ragioneSociale || !opForm.email || !opForm.dataRegistrazione) return;
    setSaving(true); setOpErrors(null);
    api.registraOperatoreAvviso(avviso.id, opForm, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_avviso_operatore_registrato")} />);
        setSaving(false); setShowNuovoOp(false);
        setOpForm({ ragioneSociale: "", email: "", pec: "", telefono: "", dataRegistrazione: TODAY, note: "" });
        loadOperatori();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setOpErrors(response.errors); },
    });
  };

  const handleCreaRichiesta = () => {
    if (!richForm.oggetto || !richForm.testo || !richForm.dataRichiesta) return;
    setSaving(true); setRichErrors(null);
    api.creaRichiestaAvviso(avviso.id, {
      ...richForm,
      operatoreId: richForm.operatoreId ? parseInt(richForm.operatoreId) : null,
    }, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_avviso_richiesta_creata")} />);
        setSaving(false); setShowNuovaRich(false);
        setRichForm({ operatoreId: "", oggetto: "", testo: "", dataRichiesta: TODAY });
        loadRichieste();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setRichErrors(response.errors); },
    });
  };

  const handlePubblicaRisposta = () => {
    if (!rispForm.testoRisposta || !rispForm.dataRisposta || !showPubblicaRisp) return;
    setSaving(true); setRispErrors(null);
    api.pubblicaRispostaAvviso(avviso.id, showPubblicaRisp, rispForm, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate("ppp_avviso_risposta_pubblicata")} />);
        setSaving(false); setShowPubblicaRisp(null);
        setRispForm({ testoRisposta: "", dataRisposta: TODAY, fileRisposta: null });
        loadRichieste();
      },
      onError:     () => setSaving(false),
      onFormError: (response) => { setSaving(false); setRispErrors(response.errors); },
    });
  };

  if (loading) return <Spinner />;
  if (!avviso)  return null;

  const isBozza      = avviso.stato === "bozza";
  const isPubblicato = avviso.stato === "pubblicato";
  const isRevocato   = avviso.stato === "revocato";
  const scadenza     = isPubblicato ? scadenzaInfo(avviso.terminePresentazioneProposte) : null;
  const versione     = avviso.versione ?? 1;

  return (
    <div className="p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => navigate("/ppp/avvisi-pubblici")}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <BsArrowLeft /> {_locate("ppp_avviso_titolo_lista")}
          </button>
          {avviso.procedimentoId && (
            <button
              onClick={() => navigate(`/ppp/${avviso.procedimentoId}`)}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              {avviso.procedimentoTitolo ?? _locate("ppp_col_procedimento")} <BsBoxArrowUpRight size={12} />
            </button>
          )}
        </div>
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight mb-1">
              {avviso.oggetto}
            </h1>
            <StatoBadge stato={avviso.stato} versione={versione} _locate={_locate} />
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
            {isBozza && can("ppp.avviso.delete") && (
              <button
                onClick={() => setShowElimina(true)}
                className="flex items-center gap-1 text-sm text-red-600 hover:text-red-800 border border-red-200 hover:border-red-400 rounded px-3 py-1.5 transition-colors"
              >
                <BsTrash size={14} /> {_locate("ppp_avviso_elimina") ?? "Elimina"}
              </button>
            )}
            {isBozza && can("ppp.avviso.publish") && (
              <SimpleButton customFunc={() => setShowPubblica(true)} text={_locate("ppp_avviso_pubblica")} />
            )}
            {isBozza && can("ppp.avviso.edit") && (
              <SimpleButton customFunc={() => openEdit(avviso)} text={_locate("ppp_modifica")} />
            )}
            {isPubblicato && can("ppp.avviso.revoke") && (
              <SimpleButton customFunc={() => setShowRevoca(true)} text={_locate("ppp_avviso_revoca")} />
            )}
            {isRevocato && can("ppp.avviso.create") && (
              <LoadingButton
                isLoading={saving}
                customFunc={handleCreaVersione}
                text={_locate("ppp_avviso_crea_versione")}
              />
            )}
          </div>
        </div>

        {/* Link avviso precedente */}
        {avviso.avvisoPrecedenteId && (
          <div className="mt-2">
            <Link
              to={`/ppp/avvisi-pubblici/${avviso.avvisoPrecedenteId}`}
              className="text-xs text-gray-500 hover:text-blue-600 flex items-center gap-1"
            >
              ← {_locate("ppp_avviso_versione_precedente")}
            </Link>
          </div>
        )}
      </div>

      {/* Banner revocato */}
      {isRevocato && (
        <div className="border border-orange-300 bg-orange-50 dark:bg-orange-900/20 rounded-lg px-4 py-3 mb-4">
          <p className="text-sm font-semibold text-orange-700 dark:text-orange-400 mb-1">
            {_locate("ppp_avviso_stato_revocato")} — {formatDate(avviso.dataRevoca)}
          </p>
          {avviso.motivoRevoca && (
            <p className="text-sm text-orange-600 dark:text-orange-300 whitespace-pre-wrap">{avviso.motivoRevoca}</p>
          )}
        </div>
      )}

      {/* Banner termine presentazione proposte */}
      {scadenza && (
        <div className={`border rounded-lg px-4 py-3 mb-4 flex items-center gap-3 ${scadenza.cls}`}>
          <span className="text-lg">{scadenza.icon}</span>
          <div>
            <span className="font-medium">{_locate("ppp_avviso_termine_proposte")}: </span>
            <span>{scadenza.label}</span>
            {avviso.terminePresentazioneProposte && (
              <span className="ml-2 text-sm opacity-80">({formatDate(avviso.terminePresentazioneProposte)})</span>
            )}
          </div>
        </div>
      )}

      {/* Informazioni generali */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-4">
          {_locate("ppp_avviso_sezione_generale")}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label={_locate("ppp_avviso_procedimento")} value={avviso.procedimentoTitolo} />
          <InfoRow label={_locate("ppp_avviso_numero_atto")} value={avviso.numeroAtto} />
          <InfoRow label={_locate("ppp_avviso_sopra_soglia_label")} value={avviso.sopraSogliaEuropea ? _locate("ppp_si") : _locate("ppp_no")} />
        </div>
      </div>

      {/* Contenuto avviso */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
        <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-4">
          {_locate("ppp_avviso_sezione_contenuto")} — Art. 193 co. 16 D.lgs 36/2023
        </h2>
        <div className="space-y-4">
          <InfoRow label={_locate("ppp_avviso_descrizione_intervento")} value={avviso.descrizioneIntervento} />
          <InfoRow label={_locate("ppp_avviso_requisiti_minimi")} value={avviso.requisitiMinimi} />
        </div>
      </div>

      {/* Allegati */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
            {_locate("ppp_avviso_allegati")}
          </h2>
          {can("ppp.avviso.attachments") && (
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder={_locate("ppp_avviso_allegato_nome_placeholder")}
                value={nuovoAllegatoNome}
                onChange={(e) => setNuovoAllegatoNome(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 w-40 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <input
                ref={fileInputRef}
                type="file"
                style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
                onChange={handleUploadAllegato}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
              >
                <BsPaperclip /> {_locate("ppp_avviso_allegato_carica")}
              </button>
            </div>
          )}
        </div>
        <NotificationsList errors={allegatiErrors} />
        {allegati.length === 0 ? (
          <p className="text-sm text-gray-400 italic">{_locate("ppp_avviso_allegati_nessuno")}</p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {allegati.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <BsPaperclip className="text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm text-gray-900 dark:text-white font-medium">{a.nome}</span>
                    <span className="ml-2 text-xs text-gray-400">{a.fileNome}</span>
                    {a.fileDimensione && (
                      <span className="ml-1 text-xs text-gray-400">({formatBytes(a.fileDimensione)})</span>
                    )}
                  </div>
                </div>
                {can("ppp.avviso.attachments") && (
                  <button
                    onClick={() => handleEliminaAllegato(a.id)}
                    className="text-red-400 hover:text-red-600 p-1 shrink-0"
                    title={_locate("ppp_avviso_allegato_elimina")}
                  >
                    <BsTrash size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pubblicazione (se pubblicato) */}
      {isPubblicato && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-5 mb-4">
          <h2 className="text-sm font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide mb-4">
            {_locate("ppp_avviso_sezione_pubblicazione")}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <InfoRow label={_locate("ppp_avviso_data_pubblicazione")} value={formatDate(avviso.dataPubblicazione)} />
            <InfoRow label={_locate("ppp_avviso_termine_proposte")} value={formatDate(avviso.terminePresentazioneProposte)} />
          </div>
          <div className="space-y-2">
            {avviso.urlAmministrazioneTrasparente && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 uppercase w-40">{_locate("ppp_avviso_url_amm_trasparente")}</span>
                <a href={avviso.urlAmministrazioneTrasparente} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate">
                  {avviso.urlAmministrazioneTrasparente}
                </a>
              </div>
            )}
            {avviso.urlBdncp && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 uppercase w-40">{_locate("ppp_avviso_url_bdncp")}</span>
                <a href={avviso.urlBdncp} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate">
                  {avviso.urlBdncp}
                </a>
              </div>
            )}
            {avviso.urlGuue && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 uppercase w-40">{_locate("ppp_avviso_url_guue")}</span>
                <a href={avviso.urlGuue} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate">
                  {avviso.urlGuue}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Note */}
      {avviso.note && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
          <InfoRow label={_locate("ppp_avviso_note")} value={avviso.note} />
        </div>
      )}

      {/* Registro operatori */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              {_locate("ppp_avviso_sezione_operatori")}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{_locate("ppp_avviso_operatori_hint")}</p>
          </div>
          {can("ppp.avviso.edit") && (
            <button
              onClick={() => setShowNuovoOp(true)}
              className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
            >
              <BsPlus className="text-base" /> {_locate("ppp_avviso_aggiungi_operatore")}
            </button>
          )}
        </div>
        {operatori.length === 0 ? (
          <p className="text-sm text-gray-400 italic">{_locate("ppp_avviso_operatori_nessuno")}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 uppercase">
                <th className="text-left py-2 pr-4 font-medium">{_locate("ppp_avviso_operatore_ragione_sociale")}</th>
                <th className="text-left py-2 pr-4 font-medium">{_locate("ppp_avviso_operatore_email")}</th>
                <th className="text-left py-2 pr-4 font-medium">{_locate("ppp_avviso_operatore_pec")}</th>
                <th className="text-left py-2 font-medium">{_locate("ppp_avviso_operatore_data_registrazione")}</th>
              </tr>
            </thead>
            <tbody>
              {operatori.map((op) => (
                <tr key={op.id} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="py-2 pr-4 font-medium">{op.ragioneSociale}</td>
                  <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">{op.email}</td>
                  <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">{op.pec || "—"}</td>
                  <td className="py-2 text-gray-600 dark:text-gray-400">{formatDate(op.dataRegistrazione)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Richieste di integrazione */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
              {_locate("ppp_avviso_sezione_richieste")}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{_locate("ppp_avviso_richieste_hint")}</p>
          </div>
          {can("ppp.avviso.edit") && (
            <button
              onClick={() => setShowNuovaRich(true)}
              className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
            >
              <BsPlus className="text-base" /> {_locate("ppp_avviso_aggiungi_richiesta")}
            </button>
          )}
        </div>
        {richieste.length === 0 ? (
          <p className="text-sm text-gray-400 italic">{_locate("ppp_avviso_richieste_nessuna")}</p>
        ) : (
          <div className="space-y-3">
            {richieste.map((r) => (
              <div key={r.id} className="border border-gray-100 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <StatoRichiestaBadge stato={r.stato} _locate={_locate} />
                      <span className="text-xs text-gray-500">{formatDate(r.dataRichiesta)}</span>
                      {r.operatoreRagioneSociale && (
                        <span className="text-xs text-gray-500">· {r.operatoreRagioneSociale}</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{r.oggetto}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-wrap">{r.testo}</p>
                  </div>
                  {r.stato === "ricevuta" && (
                    <button
                      onClick={() => { setShowPubblicaRisp(r.id); setRispErrors(null); }}
                      className="text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 whitespace-nowrap"
                    >
                      {_locate("ppp_avviso_richiesta_rispondi") ?? "Rispondi"}
                    </button>
                  )}
                </div>
                {r.stato === "risposta_pubblicata" && r.testoRisposta && (
                  <div className="mt-3 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded p-3">
                    <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">
                      {_locate("ppp_avviso_richiesta_testo_risposta")} — {formatDate(r.dataRisposta)}
                    </p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{r.testoRisposta}</p>
                    {r.fileRispostaNome && r.fileRispostaUrl && (
                      <div className="mt-2">
                        <button
                          onClick={() => download(r.fileRispostaUrl, r.fileRispostaNome)}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-2 py-1 border border-blue-200 rounded hover:bg-blue-50"
                        >
                          <BsDownload size={12} /> {r.fileRispostaNome}
                        </button>
                      </div>
                    )}
                    {r.invii && r.invii.length > 0 && (
                      <p className="text-xs text-gray-500 mt-2">
                        {_locate("ppp_avviso_richiesta_invii")}: {r.invii.map((i) => i.ragioneSociale).join(", ")}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      </div>{/* fine card */}

      {/* Modal pubblicazione avviso */}
      {showPubblica && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {_locate("ppp_avviso_pubblica")}
            </h3>
            <p className="text-sm text-gray-500 mb-4">{_locate("ppp_avviso_pubblica_hint")}</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={_locate("ppp_avviso_data_pubblicazione")}
                  type="date" required
                  value={pubForm.dataPubblicazione}
                  onChange={(e) => setPubForm((f) => ({ ...f, dataPubblicazione: e.target.value }))}
                />
                <Input
                  label={_locate("ppp_avviso_termine_proposte_label")}
                  type="date" required
                  value={pubForm.terminePresentazioneProposte}
                  onChange={(e) => setPubForm((f) => ({ ...f, terminePresentazioneProposte: e.target.value }))}
                />
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                {_locate("ppp_avviso_termine_60gg_avviso")}
              </p>
              <Input
                label={_locate("ppp_avviso_url_amm_trasparente")}
                value={pubForm.urlAmministrazioneTrasparente}
                onChange={(e) => setPubForm((f) => ({ ...f, urlAmministrazioneTrasparente: e.target.value }))}
                placeholder="https://..."
              />
              <Input
                label={_locate("ppp_avviso_url_bdncp")}
                value={pubForm.urlBdncp}
                onChange={(e) => setPubForm((f) => ({ ...f, urlBdncp: e.target.value }))}
                placeholder="https://..."
              />
              {avviso.sopraSogliaEuropea && (
                <Input
                  label={_locate("ppp_avviso_url_guue")}
                  value={pubForm.urlGuue}
                  onChange={(e) => setPubForm((f) => ({ ...f, urlGuue: e.target.value }))}
                  placeholder="https://..."
                />
              )}
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="inviaNotificaEmail"
                  checked={pubForm.inviaNotificaEmail}
                  onChange={(e) => setPubForm((f) => ({ ...f, inviaNotificaEmail: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded border-gray-300"
                />
                <label htmlFor="inviaNotificaEmail" className="text-sm text-gray-700 dark:text-gray-300">
                  {_locate("ppp_avviso_invia_notifica_email")}
                </label>
              </div>
            </div>
            <NotificationsList errors={pubErrors} />
            <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-gray-100">
              <SimpleButton customFunc={() => setShowPubblica(false)} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={pubForm.dataPubblicazione && pubForm.terminePresentazioneProposte ? handlePubblica : undefined}
                text={_locate("ppp_avviso_pubblica")}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal revoca */}
      {showRevoca && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {_locate("ppp_avviso_revoca")}
            </h3>
            <p className="text-sm text-gray-500 mb-4">{_locate("ppp_avviso_revoca_hint")}</p>
            <div className="space-y-4">
              <Input
                label={_locate("ppp_avviso_data_revoca")}
                type="date" required
                value={revocaForm.dataRevoca}
                onChange={(e) => setRevocaForm((f) => ({ ...f, dataRevoca: e.target.value }))}
              />
              <TextArea
                label={_locate("ppp_avviso_motivo_revoca")}
                rows={4} required
                value={revocaForm.motivoRevoca}
                onChange={(e) => setRevocaForm((f) => ({ ...f, motivoRevoca: e.target.value }))}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {_locate("ppp_avviso_atto_revoca")} <span className="text-red-500">*</span>
                </label>
                <input
                  ref={attoRevocaRef}
                  type="file"
                  className="text-sm text-gray-600 dark:text-gray-400 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                  onChange={(e) => setRevocaForm((f) => ({ ...f, fileAttoRevoca: e.target.files?.[0] ?? null }))}
                />
              </div>
            </div>
            <NotificationsList errors={revocaErrors} />
            <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-gray-100">
              <SimpleButton customFunc={() => setShowRevoca(false)} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={revocaForm.dataRevoca && revocaForm.motivoRevoca && revocaForm.fileAttoRevoca ? handleRevoca : undefined}
                text={_locate("ppp_avviso_revoca_conferma")}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal nuovo operatore */}
      {showNuovoOp && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {_locate("ppp_avviso_aggiungi_operatore")}
            </h3>
            <div className="space-y-3">
              <Input
                label={_locate("ppp_avviso_operatore_ragione_sociale")}
                required
                value={opForm.ragioneSociale}
                onChange={setOpF("ragioneSociale")}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label={_locate("ppp_avviso_operatore_email")}
                  type="email" required
                  value={opForm.email}
                  onChange={setOpF("email")}
                />
                <Input
                  label={_locate("ppp_avviso_operatore_pec")}
                  type="email"
                  value={opForm.pec}
                  onChange={setOpF("pec")}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label={_locate("ppp_avviso_operatore_telefono")}
                  value={opForm.telefono}
                  onChange={setOpF("telefono")}
                />
                <Input
                  label={_locate("ppp_avviso_operatore_data_registrazione")}
                  type="date" required
                  value={opForm.dataRegistrazione}
                  onChange={setOpF("dataRegistrazione")}
                />
              </div>
              <TextArea
                label={_locate("ppp_avviso_operatore_note")}
                rows={2}
                value={opForm.note}
                onChange={setOpF("note")}
              />
            </div>
            <NotificationsList errors={opErrors} />
            <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-gray-100">
              <SimpleButton customFunc={() => setShowNuovoOp(false)} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={opForm.ragioneSociale && opForm.email ? handleRegistraOperatore : undefined}
                text={_locate("ppp_avviso_salva")}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal nuova richiesta */}
      {showNuovaRich && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {_locate("ppp_avviso_aggiungi_richiesta")}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {_locate("ppp_avviso_richiesta_operatore")}
                </label>
                <Select
                  value={richForm.operatoreId}
                  onChange={(e) => setRichForm((f) => ({ ...f, operatoreId: e.target.value }))}
                  options={[
                    { value: "", label: "— Operatore non registrato —" },
                    ...operatori.map((op) => ({ value: op.id, label: op.ragioneSociale })),
                  ]}
                />
              </div>
              <Input
                label={_locate("ppp_avviso_richiesta_oggetto")}
                required
                value={richForm.oggetto}
                onChange={setRichF("oggetto")}
              />
              <TextArea
                label={_locate("ppp_avviso_richiesta_testo")}
                rows={4} required
                value={richForm.testo}
                onChange={setRichF("testo")}
              />
              <Input
                label={_locate("ppp_avviso_richiesta_data")}
                type="date" required
                value={richForm.dataRichiesta}
                onChange={setRichF("dataRichiesta")}
              />
            </div>
            <NotificationsList errors={richErrors} />
            <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-gray-100">
              <SimpleButton customFunc={() => setShowNuovaRich(false)} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={richForm.oggetto && richForm.testo ? handleCreaRichiesta : undefined}
                text={_locate("ppp_avviso_salva")}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal risposta richiesta integrazione */}
      {showPubblicaRisp && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {_locate("ppp_avviso_richiesta_rispondi") ?? "Rispondi alla richiesta"}
            </h3>
            <p className="text-sm text-gray-500 mb-4">{_locate("ppp_avviso_richiesta_pubblica_hint")}</p>
            <div className="space-y-3">
              <TextArea
                label={_locate("ppp_avviso_richiesta_testo_risposta")}
                rows={5} required
                value={rispForm.testoRisposta}
                onChange={setRispF("testoRisposta")}
              />
              <Input
                label={_locate("ppp_avviso_richiesta_data_risposta")}
                type="date" required
                value={rispForm.dataRisposta}
                onChange={setRispF("dataRisposta")}
              />
              {/* Allegato opzionale */}
              <div>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {_locate("ppp_avviso_risposta_allegato") ?? "Allegato (opzionale)"}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => rispFileRef.current?.click()}
                    className="text-xs px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
                  >
                    {rispForm.fileRisposta
                      ? rispForm.fileRisposta.name
                      : (_locate("ppp_avviso_allegato_carica") ?? "Scegli file...")}
                  </button>
                  {rispForm.fileRisposta && (
                    <button
                      type="button"
                      onClick={() => setRispForm((f) => ({ ...f, fileRisposta: null }))}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      ✕
                    </button>
                  )}
                </div>
                <input
                  ref={rispFileRef}
                  type="file"
                  style={{ position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none' }}
                  onChange={(e) => setRispForm((f) => ({ ...f, fileRisposta: e.target.files?.[0] ?? null }))}
                />
              </div>
              {operatori.length > 0 && (
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                  La risposta verrà notificata a {operatori.length} operatore{operatori.length !== 1 ? "i" : ""} registrat{operatori.length !== 1 ? "i" : "o"}: {operatori.map((o) => o.ragioneSociale).join(", ")}
                </p>
              )}
            </div>
            <NotificationsList errors={rispErrors} />
            <div className="flex gap-2 justify-end mt-4 pt-4 border-t border-gray-100">
              <SimpleButton customFunc={() => setShowPubblicaRisp(null)} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={rispForm.testoRisposta && rispForm.dataRisposta ? handlePubblicaRisposta : undefined}
                text={_locate("ppp_avviso_richiesta_rispondi") ?? "Rispondi"}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal conferma eliminazione avviso */}
      {showElimina && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
              {_locate("ppp_avviso_elimina_titolo") ?? "Elimina avviso"}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
              {_locate("ppp_avviso_elimina_conferma") ?? "Sei sicuro di voler eliminare questo avviso? L'operazione non è reversibile."}
            </p>
            <div className="flex gap-2 justify-end">
              <SimpleButton customFunc={() => setShowElimina(false)} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={handleElimina}
                text={_locate("ppp_avviso_elimina") ?? "Elimina"}
              />
            </div>
          </div>
        </div>
      )}

      {/* FormDialog modifica */}
      {editForm && (
        <FormDialog title={_locate("ppp_modifica")} size="w-[640px]">
          <div className="space-y-4">
            <Input
              label={_locate("ppp_avviso_oggetto")}
              required
              value={editForm.oggetto}
              onChange={setEditF("oggetto")}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {_locate("ppp_avviso_procedimento")}
              </label>
              <Select
                value={editForm.procedimentoId}
                onChange={(e) => setEditForm((f) => ({ ...f, procedimentoId: e.target.value }))}
                options={[
                  { value: "", label: _locate("ppp_avviso_procedimento_placeholder") },
                  ...(procedimenti ?? []).map((p) => ({ value: p.id, label: p.titolo })),
                ]}
              />
            </div>
            <TextArea
              label={_locate("ppp_avviso_descrizione_intervento")}
              rows={4}
              value={editForm.descrizioneIntervento}
              onChange={setEditF("descrizioneIntervento")}
            />
            <TextArea
              label={_locate("ppp_avviso_requisiti_minimi")}
              rows={4}
              value={editForm.requisitiMinimi}
              onChange={setEditF("requisitiMinimi")}
            />
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sopraSogliaEuropeaEdit"
                checked={editForm.sopraSogliaEuropea}
                onChange={setCheck("sopraSogliaEuropea")}
                className="w-4 h-4 text-blue-600 rounded border-gray-300"
              />
              <label htmlFor="sopraSogliaEuropeaEdit" className="text-sm text-gray-700 dark:text-gray-300">
                {_locate("ppp_avviso_sopra_soglia_europea")}
              </label>
            </div>
            <Input
              label={_locate("ppp_avviso_numero_atto")}
              value={editForm.numeroAtto}
              onChange={setEditF("numeroAtto")}
              placeholder="Es. Det. Dir. n. 42/2026"
            />
            <TextArea label={_locate("ppp_avviso_note")} rows={2} value={editForm.note} onChange={setEditF("note")} />
            <NotificationsList errors={editErrors} />
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <SimpleButton customFunc={() => setOpenForm(false)} text={_locate("ppp_annulla")} />
              <LoadingButton
                isLoading={saving}
                customFunc={editForm.oggetto ? handleAggiorna : undefined}
                text={_locate("ppp_avviso_salva")}
              />
            </div>
          </div>
        </FormDialog>
      )}
    </div>
  );
}
